/**
 * Pick a legible ink (near-black or white) for an ARBITRARY fill, from that
 * fill's own WCAG relative luminance.
 *
 * Two surfaces in this app are painted a colour nobody on the design side
 * chose: the tenant's brand hex behind the logo monogram, and a user-picked
 * leave-type chip. Neither can take its ink from a design token, and the reason
 * is structural rather than a matter of taste — a token flips with the theme
 * while these fills do not, so the ink inverts out from under a fill that stays
 * put. The monogram shipped exactly that way, pairing the frozen tenant hex
 * with `--primary-foreground`: on the live tenant colour #0066FF it measured
 * 4.83:1 in light and 2.73:1 in dark, off the same fill.
 *
 * Deriving the ink from the fill instead makes the pair theme-independent by
 * construction. Both candidates are measured against the actual fill and the
 * better one wins, so the result is the best of the two at every luminance
 * rather than the one a fixed cut-off happened to name. (A plain `L > 0.5`
 * split — which is what this started as — hands mid-tones to white well past
 * the point where black is the better read: on #ff9800 it chose white at
 * 2.16:1 over near-black at 8.08:1.)
 *
 * The floor this can promise is the crossover itself, 4.17:1 at L≈0.202, where
 * neither ink clears 4.5:1. No two-candidate rule can do better; a fill sitting
 * in that band is the fill's problem, not the ink's.
 */

/** Near-black rather than pure black: softer on a saturated fill, same duty. */
export const INK_DARK = '#1a1a1a';
export const INK_LIGHT = '#ffffff';

/** `#abc` / `#aabbcc` (with or without the `#`) → channels, else null. */
function parseHex(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** WCAG 2.1 relative luminance (§ dfn-relative-luminance). */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function ratio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const L_DARK = relativeLuminance(parseHex(INK_DARK)!);
const L_LIGHT = relativeLuminance(parseHex(INK_LIGHT)!);

/**
 * The better-measuring of `INK_DARK` / `INK_LIGHT` on the given fill.
 *
 * Anything that is not a hex colour — null, a CSS `var()`, an `rgb()` string —
 * falls back to `INK_DARK`, which is the safe half of the pair on the pale
 * defaults these call sites see before real data arrives.
 */
export function readableOn(bg: string | null | undefined): string {
  const rgb = bg ? parseHex(bg) : null;
  if (!rgb) return INK_DARK;
  const L = relativeLuminance(rgb);
  return ratio(L, L_DARK) >= ratio(L, L_LIGHT) ? INK_DARK : INK_LIGHT;
}

/** Measured contrast of `readableOn(bg)` against `bg`. Exposed for tests. */
export function readableRatioOn(bg: string): number {
  const rgb = parseHex(bg);
  if (!rgb) return 0;
  return ratio(relativeLuminance(rgb), relativeLuminance(parseHex(readableOn(bg))!));
}


/* ===========================================================================
 * LIFTING A TENANT'S BRAND UNTIL IT READS ON THE NAV RAIL.
 *
 * The rail is INK IN BOTH THEMES (`--nav-surface`, #1B1A21 light / #131210
 * dark) and the brand appears on it exactly twice: the 3px bar down the current
 * row, and that row's glyph. Both are `--nav-active`, and `--nav-active` is not
 * `--primary` — at the seed's #5323e6 the brand is far too dark against the
 * rail to mark anything. The token ships the seed hue LIFTED, #9d84ff.
 *
 * That lift was done by hand, once, for the seed. So a tenant who sets a brand
 * colour in Settings → Branding recoloured their logo tile and NOT the marker
 * that says where they are: the rail went on wearing Hadir violet on somebody
 * else's install. The store could not simply write the tenant hex through — a
 * raw hex on this rail is anywhere from invisible (#0047D6 lands at 1.76:1) to
 * fine, and there is no way to know which without measuring.
 *
 * So it is measured. Same move `readableOn` above makes for the monogram, and
 * for the same structural reason: a fill nobody on the design side chose cannot
 * take its partner from a token.
 *
 * MEASURED AGAINST THE COMPOSITE, NOT THE BARE RAIL, and that is the whole
 * subtlety. The marker is never drawn on the rail alone — it is drawn on the
 * CURRENT row, which sits on a `/10` white wash. index.css records the
 * migration contract asking for #7d5bff here and being refused for exactly
 * this: 3.96:1 on the bare rail, 2.96:1 over the wash, under the 3:1 WCAG
 * 1.4.11 wants of the one graphic that says where you are.
 * ======================================================================== */

/** The rail, per index.css. Light is the binding case — see `NAV_ACTIVE_GROUND`. */
const NAV_SURFACE_LIGHT: [number, number, number] = [27, 26, 33]; // #1B1A21
const NAV_SURFACE_DARK: [number, number, number] = [19, 18, 16]; // #131210

/** The `/10` white state layer the current row wears. */
function overWhiteWash(bg: [number, number, number]): [number, number, number] {
  return bg.map((c) => Math.round(0.1 * 255 + 0.9 * c)) as [number, number, number];
}

/**
 * The ground the marker is actually measured on: the current row's composite,
 * in whichever theme makes it hardest. A lighter ground gives a light marker
 * LESS contrast, so the light rail's composite (#323137) binds over the dark
 * rail's (#2B2A28) and clearing it clears both.
 */
const NAV_ACTIVE_GROUND = Math.max(
  relativeLuminance(overWhiteWash(NAV_SURFACE_LIGHT)),
  relativeLuminance(overWhiteWash(NAV_SURFACE_DARK))
);

/**
 * What a lifted marker has to reach, and why it is not the bare 3:1 floor.
 *
 * 4.4:1 is what the hand-lifted seed (#9d84ff) actually measures over that
 * composite. Targeting the floor instead would let a tenant's rail ship a
 * marker visibly weaker than the one we ship ourselves, which is a promise
 * quietly broken rather than a rule quietly met.
 */
export const NAV_ACTIVE_MIN_RATIO = 4.4;

/** sRGB → HSL, on 0..1 channels. Only `l` is moved; `h`/`s` are the brand. */
function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === R
      ? ((G - B) / d + (G < B ? 6 : 0)) / 6
      : max === G
        ? ((B - R) / d + 2) / 6
        : ((R - G) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}

/**
 * A tenant's brand hex, lifted until it marks the current nav row legibly —
 * or `null` when it cannot be, which is a real answer and not a failure.
 *
 * HUE AND SATURATION ARE LEFT ALONE. Only lightness moves, in 1% steps, so the
 * result is recognisably the tenant's colour rather than a colour we invented
 * for them. A brand already bright enough is returned untouched.
 *
 * `null` is the answer for anything that is not a hex — the caller then keeps
 * the seed token rather than shipping an unreadable marker. Probed over the
 * whole colour cube, every real colour comes back liftable: a dark one is
 * raised, a light one is already there. An ACHROMATIC brand (black, a deep
 * grey) is lifted to a light grey, which is the honest outcome — it is legible,
 * and a tenant who chose no hue cannot be given one.
 *
 * The 92% cap bounds the search, not the answer: a colour above it is returned
 * as it stands by the check above.
 */
export function liftForNavActive(hex: string | null | undefined): [number, number, number] | null {
  const rgb = hex ? parseHex(hex) : null;
  if (!rgb) return null;
  /* THE INPUT IS TESTED BEFORE THE CAP, not inside the loop, and that ordering
     is a bug fix rather than a tidy-up. Starting the loop at `l0` and bounding
     it at 0.92 meant a brand ALREADY lighter than the cap never entered the
     loop at all and came back `null` — so a tenant on white or a very pale
     brand would have kept the seed violet despite their colour being the most
     legible thing on the rail (white measures ~14:1 there). Probed across the
     colour cube, that was the only family the lift refused, and every member of
     it needed no lifting. */
  if (ratio(relativeLuminance(rgb), NAV_ACTIVE_GROUND) >= NAV_ACTIVE_MIN_RATIO) {
    return rgb;
  }
  const [h, s, l0] = rgbToHsl(rgb);
  for (let l = l0; l <= 0.92; l += 0.01) {
    const candidate = hslToRgb([h, s, l]);
    if (ratio(relativeLuminance(candidate), NAV_ACTIVE_GROUND) >= NAV_ACTIVE_MIN_RATIO) {
      return candidate;
    }
  }
  return null;
}

/** Measured contrast of a marker colour over the current row. For tests. */
export function navActiveRatio(rgb: [number, number, number]): number {
  return ratio(relativeLuminance(rgb), NAV_ACTIVE_GROUND);
}
